// DS EDIT 9 — deployment-freshness orchestration (non-visual).
// Checks /deployment-meta.json at startup, on visibility, every 15 minutes
// while active, and on reconnect. Failures are SILENT with bounded
// exponential backoff. Requests are credentialless, same-origin, no-store,
// and carry zero user-derived data (data-blind by construction).
import { useCallback, useEffect, useRef, useState } from "react";
import {
  computeBackoffMs,
  isNewerBuild,
  parseDeploymentMeta,
  type DeploymentMeta,
} from "@/lib/deploymentFreshnessPure";

const META_URL = "/deployment-meta.json";
const CHECK_INTERVAL_MS = 15 * 60_000;

/** Baked in at build time — same build that wrote /deployment-meta.json. */
export const CURRENT_BUILD_ID: string =
  (import.meta.env.VITE_APP_BUILD_ID as string | undefined) || "dev";

// Module-level so simultaneous triggers (and multiple mounts) share ONE
// request pipeline and one backoff state — no request storms.
let inflight: Promise<DeploymentMeta | null> | null = null;
let consecutiveFailures = 0;
let nextAllowedAt = 0;

async function fetchDeploymentMeta(): Promise<DeploymentMeta | null> {
  if (inflight) return inflight;
  if (Date.now() < nextAllowedAt) return null; // still backing off
  inflight = (async () => {
    try {
      const res = await fetch(META_URL, {
        cache: "no-store",
        credentials: "omit",
      });
      if (!res.ok) throw new Error(String(res.status));
      const meta = parseDeploymentMeta(await res.json());
      if (!meta) throw new Error("invalid metadata");
      consecutiveFailures = 0;
      nextAllowedAt = 0;
      return meta;
    } catch {
      consecutiveFailures += 1;
      nextAllowedAt = Date.now() + computeBackoffMs(consecutiveFailures, Math.random());
      return null; // silent — never a toast, never blocks startup
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function useDeploymentFreshness() {
  const [availableMeta, setAvailableMeta] = useState<DeploymentMeta | null>(null);
  const dismissedBuildRef = useRef<string | null>(null);
  // Dev servers have no deployment metadata; local builds have no real id.
  const disabled =
    import.meta.env.DEV || CURRENT_BUILD_ID === "dev" || CURRENT_BUILD_ID.startsWith("local-");

  const runCheck = useCallback(async () => {
    if (disabled) return;
    const meta = await fetchDeploymentMeta();
    if (!meta) return;
    if (meta.buildId === dismissedBuildRef.current) return; // Later'd this build
    if (isNewerBuild(CURRENT_BUILD_ID, meta.buildId)) {
      setAvailableMeta(meta);
    }
  }, [disabled]);

  useEffect(() => {
    if (disabled) return;
    void runCheck(); // startup

    const onVisibility = () => {
      if (document.visibilityState === "visible") void runCheck();
    };
    const onOnline = () => void runCheck();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") void runCheck(); // skip hidden tabs
    }, CHECK_INTERVAL_MS);

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [disabled, runCheck]);

  /** "Later": suppress this build for the rest of the tab session; a different build re-prompts. */
  const dismiss = useCallback(() => {
    setAvailableMeta((current) => {
      if (current) dismissedBuildRef.current = current.buildId;
      return null;
    });
  }, []);

  return {
    updateAvailable: availableMeta !== null,
    meta: availableMeta,
    dismiss,
  };
}
